/**
 * AttackListGeneratorDialog
 *
 * 「今攻めるべきリスト自動生成」のメインダイアログ。
 * URLを入力してAI解析を実行し、結果を表示する4ステップのウィザード形式。
 *
 * ## ステップ（2段階API対応）
 * 1. URL入力: ユーザーがLPやウェブページのURLを入力
 * 2. URL解析中: AIがページを解析中（Step 1 API）
 * 3. タグ付け中: 進捗バーを表示しながらキーマンを検索中（Step 2 API）
 * 4. 完了: タグ付け件数を表示（カウントアップアニメーション付き）
 *
 * ## アニメーション
 * - タグ付け中は進捗バーでイージングアニメーション表示
 * - 完了時の件数はカウントアップアニメーションで表示
 *
 * ## 関連コンポーネント
 * - AttackListGeneratorControl: サイドバーのトリガーボタン
 * - ExecutiveList: 親コンポーネント（状態管理・API呼び出し）
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Typography from 'material-ui/Typography';
import Dialog, { DialogActions, DialogContent, DialogContentText, DialogTitle } from 'material-ui/Dialog';
import MuiButton from 'material-ui/Button';
import DialogModal from '../../common/DialogModal';
import TextField from 'material-ui/TextField';
import Button from '../../Parts/Button';
import Loader from '../../Parts/Loader';

// ウィザードのステップ定数（2段階API対応）
const STEP_URL_INPUT = 1;
const STEP_ANALYZING_URL = 2;
const STEP_EXECUTING_TAGGING = 3;
const STEP_COMPLETE = 4;

// タイマー間隔（ミリ秒）
const TIMER_INTERVAL_MS = 100;
// 進捗バー関連
const PROGRESS_DURATION_MS = 30000; // 30秒で95%まで
const PROGRESS_MAX_BEFORE_COMPLETE = 95; // 完了前の最大進捗
const PROGRESS_COMPLETE = 100;
const PROGRESS_EASING_POWER = 3; // イージングの強さ

const styles = () => ({
  paper: {
    maxHeight: 'unset',
    background: "#F4F6F9",
    width: '600px',
  },

  contentContainer: {
    padding: '25px',
    margin: 0,
  },

  container: {
    background: '#FFFFFF',
    border: '1px solid #D8DBDF',
    padding: '20px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  row: {
    marginTop: '20px',
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },

  fullWidth: {
    width: '100%',
  },

  input: {
    boxSizing: 'border-box',
  },

  buttonRow: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
  },

  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },

  loaderText: {
    marginTop: '70px',
    fontSize: '14px',
    color: '#666',
  },

  successMessage: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: '20px',
  },

  errorMessage: {
    fontSize: '14px',
    color: '#f44336',
    marginTop: '10px',
  },

  sectionTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333',
  },

  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
  },

  chipRoot: {
    backgroundColor: 'rgba(26,101,255,.05)',
    border: '#1A65FF solid 1px',
    color: '#181819',
    height: '24px',
    borderRadius: '2px',
  },

  chipLabel: {
    padding: '2px 8px',
    fontSize: '11px',
  },

  summaryText: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.5',
    wordBreak: 'break-word',
  },

  taggedCount: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginTop: '10px',
  },

  supplementText: {
    fontSize: '12px',
    color: '#888',
    marginTop: '8px',
  },

  rangeText: {
    fontSize: '12px',
    color: '#666',
  },

  taggingLoaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '25px',
    padding: '16px 20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    width: '100%',
    boxSizing: 'border-box',
  },

  taggingLoaderText: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '12px',
  },

  progressBarContainer: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  },

  progressBar: {
    height: '100%',
    backgroundColor: '#1A65FF',
    borderRadius: '4px',
    transition: 'width 0.3s ease-out',
  },

  progressText: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1A65FF',
  },

  countUpNumber: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A65FF',
  },

  countUpLabel: {
    fontSize: '16px',
    color: '#333',
  },

  resultIcon: {
    fontSize: '24px',
    marginRight: '8px',
  },
});

class AttackListGeneratorDialog extends React.Component {
  state = {
    url: '',
    step: STEP_URL_INPUT,
    urlError: '',
    countUpValue: 0,
    countUpStarted: false,
    taggingProgress: 0, // タグ付け進捗（0-100）
    showCloseConfirm: false, // 閉じる確認ダイアログ表示
  }

  // 進捗タイマーのID
  progressTimerId = null;

  /**
   * ステップ遷移のハンドリング（2段階API対応）
   *
   * Phase 1: URL解析
   * - isAnalyzingUrl: true → false に変わった時
   * - attackListAnalysisId が存在 → 成功、Step 2（タグ付け）を自動開始
   * - attackListError が存在 → 失敗、Step 1（入力）に戻る
   *
   * Phase 2: タグ付け
   * - isExecutingTagging: true → false に変わった時
   * - attackListTag が存在 → 成功、Step 4（完了）へ
   * - attackListError が存在 → 失敗、エラー表示（解析結果は維持）
   */
  componentWillReceiveProps(nextProps) {
    // Phase 1完了: URL解析
    if (this.props.isAnalyzingUrl && !nextProps.isAnalyzingUrl) {
      if (nextProps.attackListAnalysisId && nextProps.attackListExtractedConditions) {
        // 解析成功 → タグ付けステップへ遷移し、自動的にタグ付け開始
        this.setState({ step: STEP_EXECUTING_TAGGING, taggingProgress: 0 });
        // 進捗タイマー開始
        this.startProgressTimer();
        // タグ付けAPIを呼び出し
        this.props.postAttackListExecuteTagging({
          analysis_id: nextProps.attackListAnalysisId
        });
      } else if (nextProps.attackListError) {
        // 解析失敗 → 入力画面に戻る
        this.setState({ step: STEP_URL_INPUT });
      }
    }

    // Phase 2完了: タグ付け
    if (this.props.isExecutingTagging && !nextProps.isExecutingTagging) {
      // 進捗タイマー停止
      this.stopProgressTimer();
      if (nextProps.attackListTag) {
        // タグ付け成功 → 進捗を100%にして完了画面へ
        this.setState({ taggingProgress: PROGRESS_COMPLETE });
        const MIN_DISPLAY_TIME_MS = 1500;
        setTimeout(() => {
          this.setState({ step: STEP_COMPLETE, countUpStarted: false, countUpValue: 0 });
          // カウントアップアニメーション開始
          this.startCountUp(nextProps.attackListTaggedCount || 0);
        }, MIN_DISPLAY_TIME_MS);
      } else if (nextProps.attackListError) {
        // タグ付け失敗 → エラー表示（解析結果は表示したまま）
        // ステップはそのまま維持
      }
    }
  }

  /**
   * 進捗タイマー開始
   * 0%から95%まで約30秒かけて進む（実際の完了時に100%になる）
   */
  startProgressTimer = () => {
    this.stopProgressTimer();
    const startTime = Date.now();

    this.progressTimerId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / PROGRESS_DURATION_MS, 1);
      // イージング（最初は速く、後半は遅く）
      const eased = 1 - Math.pow(1 - progress, PROGRESS_EASING_POWER);
      const percentage = Math.floor(eased * PROGRESS_MAX_BEFORE_COMPLETE);

      this.setState({ taggingProgress: percentage });

      if (percentage >= PROGRESS_MAX_BEFORE_COMPLETE) {
        this.stopProgressTimer();
      }
    }, TIMER_INTERVAL_MS);
  }

  /**
   * 進捗タイマー停止
   */
  stopProgressTimer = () => {
    if (this.progressTimerId) {
      clearInterval(this.progressTimerId);
      this.progressTimerId = null;
    }
  }

  componentWillUnmount() {
    this.stopProgressTimer();
  }

  /**
   * カウントアップアニメーション
   * 完了時に件数を0からカウントアップして表示
   */
  startCountUp = (endValue) => {
    if (this.state.countUpStarted) return;
    this.setState({ countUpStarted: true });

    const duration = 1000; // 1秒
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutQuad
      const eased = 1 - (1 - progress) * (1 - progress);
      const currentValue = Math.floor(endValue * eased);

      this.setState({ countUpValue: currentValue });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  handleUrlChange = (e) => {
    const url = e.target.value;
    this.setState({ url, urlError: '' });
  }

  validateUrl = (url) => {
    const urlPattern = /^https?:\/\/.+/i;
    return urlPattern.test(url);
  }

  /**
   * 解析開始（2段階APIのStep 1）
   * - URLのバリデーションを実行
   * - ステップ2（URL解析中）に遷移
   * - API呼び出し（postAttackListAnalyzeUrl）
   */
  handleAnalyze = () => {
    const { url } = this.state;

    if (!url) {
      this.setState({ urlError: 'URLを入力してください' });
      return;
    }

    if (!this.validateUrl(url)) {
      this.setState({ urlError: '有効なURLを入力してください（http:// または https:// で始まる必要があります）' });
      return;
    }

    this.setState({ step: STEP_ANALYZING_URL });
    this.props.postAttackListAnalyzeUrl({ url });
  }

  /**
   * キャンセル（ローディング中）
   * - 状態をクリアしてステップ1に戻る
   * - ダイアログは閉じない（再入力可能）
   */
  handleCancel = () => {
    this.props.clearAttackListState();
    this.setState({
      step: STEP_URL_INPUT,
      url: '',
      urlError: '',
      countUpValue: 0,
      countUpStarted: false,
    });
  }

  /**
   * リストを表示
   * - onTagCreatedコールバックでタグ名を親コンポーネントに通知
   * - 親コンポーネント（ExecutiveList）がサイドバーにタグをチェックして検索実行
   * - その後ダイアログを閉じる
   */
  handleShowList = () => {
    const { attackListTag, onTagCreated } = this.props;

    if (attackListTag && onTagCreated) {
      // タグ名とIDを両方渡す（ホット順ソートに必要）
      onTagCreated(attackListTag.name, attackListTag.id);
    }

    this.handleClose();
  }

  /**
   * 処理中かどうかを判定
   */
  isProcessing = () => {
    const { step } = this.state;
    return step === STEP_ANALYZING_URL || step === STEP_EXECUTING_TAGGING;
  }

  /**
   * ダイアログを閉じる（確認付き）
   * 処理中の場合は確認ダイアログを表示
   */
  handleClose = () => {
    if (this.isProcessing()) {
      this.setState({ showCloseConfirm: true });
      return;
    }
    this.executeClose();
  }

  /**
   * 閉じる確認をキャンセル
   */
  handleCloseConfirmCancel = () => {
    this.setState({ showCloseConfirm: false });
  }

  /**
   * 閉じる確認でOKを押した時
   */
  handleCloseConfirmOk = () => {
    this.setState({ showCloseConfirm: false });
    this.executeClose();
  }

  /**
   * 実際にダイアログを閉じる処理
   */
  executeClose = () => {
    this.stopProgressTimer();
    this.props.clearAttackListState();
    this.setState({
      step: STEP_URL_INPUT,
      url: '',
      urlError: '',
      countUpValue: 0,
      countUpStarted: false,
      showCloseConfirm: false,
    });
    this.props.toggleDialog();
  }

  getErrorMessage = (error) => {
    if (!error) {
      return '';
    }
    // APIレスポンスは { error: { code, message } } の形式
    const errorData = error.error || error;
    const errorMessages = {
      'INVALID_URL': 'URLの形式が正しくありません',
      'URL_FETCH_FAILED': '指定されたURLにアクセスできませんでした。セキュリティ設定により一部のサイトは解析できない場合があります。別のURLをお試しください。',
      'PAGE_PARSE_FAILED': 'ページの内容を解析できませんでした。別のURLをお試しください。',
      'AI_SERVICE_ERROR': '現在サービスが混み合っています。しばらく経ってからお試しください',
      'TIMEOUT': '解析に時間がかかっています。しばらく経ってからお試しください',
      'NO_MATCH': '条件に該当するキーマンが見つかりませんでした',
      'ANALYSIS_EXPIRED': '解析結果の有効期限が切れました。再度URLを入力してください',
      'ANALYSIS_NOT_FOUND': '解析結果が見つかりませんでした。再度URLを入力してください',
    };
    return errorMessages[errorData.code] || errorData.message || '予期しないエラーが発生しました';
  }

  formatNumber = (num) => {
    if (num === null || num === undefined) {
      return '指定なし';
    }
    return num.toLocaleString();
  }

  renderStep1 = () => {
    const { classes, attackListError } = this.props;
    const { url, urlError } = this.state;

    return (
      <div className={classes.container}>
        <Typography variant="body1" style={{ marginBottom: '20px' }}>
          解析したいページのURLを入力してください。<br />
          AIがページの内容を解析し、ターゲットとなるキーマンを自動で抽出します。
        </Typography>

        <TextField
          label="URL"
          placeholder="https://example.com/lp"
          value={url}
          onChange={this.handleUrlChange}
          className={classes.fullWidth}
          error={!!urlError}
          helperText={urlError}
        />

        {attackListError && (
          <Typography className={classes.errorMessage}>
            {this.getErrorMessage(attackListError)}
          </Typography>
        )}

        <div className={classes.buttonRow}>
          <Button
            type="ghost"
            text="キャンセル"
            handleClickButton={this.handleClose}
            style={{ background: '#ECEDEF', color: '#181819' }}
          />
          <Button
            text="解析開始"
            handleClickButton={this.handleAnalyze}
          />
        </div>
      </div>
    );
  }

  /**
   * Step 2: URL解析中
   */
  renderStep2_AnalyzingUrl = () => {
    const { classes } = this.props;

    return (
      <div className={classes.loaderContainer}>
        <Loader type="color" size="large" />
        <Typography className={classes.loaderText}>
          ページを解析中...
        </Typography>
        <Typography className={classes.loaderText} style={{ fontSize: '12px', marginTop: '8px' }}>
          30秒ほどかかる場合があります
        </Typography>
        <div className={classes.buttonRow}>
          <Button
            type="ghost"
            text="キャンセル"
            handleClickButton={this.handleClose}
            style={{ background: '#ECEDEF', color: '#181819' }}
          />
        </div>
      </div>
    );
  }

  /**
   * Step 3: タグ付け中（進捗バー表示）
   */
  renderStep3_ExecutingTagging = () => {
    const { classes, attackListError } = this.props;

    return (
      <div className={classes.container}>
        {/* タグ付け中の進捗表示 */}
        <div className={classes.taggingLoaderContainer}>
          <Typography className={classes.taggingLoaderText}>
            キーマンを検索中...
          </Typography>
          <div className={classes.progressBarContainer}>
            <div
              className={classes.progressBar}
              style={{ width: `${this.state.taggingProgress}%` }}
            />
          </div>
          <Typography className={classes.progressText}>
            {this.state.taggingProgress}%
          </Typography>
        </div>

        {/* エラー表示（タグ付け時のエラー） */}
        {attackListError && (
          <Typography className={classes.errorMessage} style={{ marginTop: '15px' }}>
            {this.getErrorMessage(attackListError)}
          </Typography>
        )}

        <div className={classes.buttonRow}>
          <Button
            type="ghost"
            text="キャンセル"
            handleClickButton={this.handleClose}
            style={{ background: '#ECEDEF', color: '#181819' }}
          />
        </div>
      </div>
    );
  }

  /**
   * Step 4: 完了（カウントアップアニメーション付き）
   */
  renderStep4_Complete = () => {
    const { classes } = this.props;
    const { countUpValue } = this.state;

    return (
      <div className={classes.container}>
        <Typography className={classes.successMessage}>
          ターゲットリストができました<span role="img" aria-label="お祝い">🎉🎉🎉</span>
        </Typography>

        {/* カウントアップ表示 */}
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <span className={classes.resultIcon} role="img" aria-label="target">🎯</span>
          <span className={classes.countUpNumber}>
            {countUpValue.toLocaleString()}
          </span>
          <span className={classes.countUpLabel}>
            {' '}件のキーマンを発見！
          </span>
        </div>

        <Typography className={classes.supplementText}>
          ※ 商談獲得実績や資料送付状況も考慮してリストを生成しています
        </Typography>

        <div className={classes.buttonRow}>
          <Button
            type="ghost"
            text="閉じる"
            handleClickButton={this.handleClose}
            style={{ background: '#ECEDEF', color: '#181819' }}
          />
          <Button
            text="リストを表示"
            handleClickButton={this.handleShowList}
          />
        </div>
      </div>
    );
  }

  /**
   * 閉じる確認ダイアログ
   */
  renderCloseConfirmDialog = () => {
    const { showCloseConfirm } = this.state;

    return (
      <Dialog
        open={showCloseConfirm}
        onClose={this.handleCloseConfirmCancel}
      >
        <DialogTitle>処理を中断しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText>
            現在処理を実行中です。<br />
            ダイアログを閉じても、バックグラウンドで処理は継続されます。<br />
            作成されたタグは後からリストで確認できます。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={this.handleCloseConfirmCancel}>
            キャンセル
          </MuiButton>
          <MuiButton onClick={this.handleCloseConfirmOk} color="primary">
            閉じる
          </MuiButton>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { classes, isOpen } = this.props;
    const { step } = this.state;

    const titles = {
      [STEP_URL_INPUT]: '今攻めるべきリスト自動生成',
      [STEP_ANALYZING_URL]: '解析中',
      [STEP_EXECUTING_TAGGING]: 'キーマン検索中',
      [STEP_COMPLETE]: '完了',
    };

    return (
      <React.Fragment>
        <DialogModal
          open={isOpen}
          onClose={this.handleClose}
          title={titles[step]}
          classes={{
            paper: classes.paper,
            contentContainer: classes.contentContainer,
          }}
        >
          {step === STEP_URL_INPUT && this.renderStep1()}
          {step === STEP_ANALYZING_URL && this.renderStep2_AnalyzingUrl()}
          {step === STEP_EXECUTING_TAGGING && this.renderStep3_ExecutingTagging()}
          {step === STEP_COMPLETE && this.renderStep4_Complete()}
        </DialogModal>
        {this.renderCloseConfirmDialog()}
      </React.Fragment>
    );
  }
}

AttackListGeneratorDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  // 2段階API
  postAttackListAnalyzeUrl: PropTypes.func.isRequired,
  postAttackListExecuteTagging: PropTypes.func.isRequired,
  clearAttackListState: PropTypes.func.isRequired,
  onTagCreated: PropTypes.func,
  // 2段階API用State
  isAnalyzingUrl: PropTypes.bool,
  isExecutingTagging: PropTypes.bool,
  attackListAnalysisId: PropTypes.string,
  // 結果
  attackListTag: PropTypes.object,
  attackListExtractedConditions: PropTypes.object,
  attackListSummary: PropTypes.string,
  attackListTaggedCount: PropTypes.number,
  attackListError: PropTypes.object,
};

export default withStyles(styles)(AttackListGeneratorDialog);



// WEBPACK FOOTER //
// ./src/components/ExecutiveList/dialogs/AttackListGeneratorDialog.jsx